<template>
  <div
    class="document-tag"
    :class="{ overview: !document }"
    :style="maxWidth ? { maxWidth: typeof maxWidth === 'number' ? maxWidth + 'px' : maxWidth } : {}"
    @click="onClick"
  >
    <BaseIcon v-if="icon" class="document-tag__icon" :name="getDocumentIcon(document)" />
    <div
      class="document-tag__text"
      v-tooltip="{ content: !actionable ? '' : document.name, placement: 'top', offset: '8px', delay: 400 }"
    >
      {{ isShort ? documentsLength : document.name }}
    </div>

    <BaseIcon
      v-if="isUsedByAi"
      class="document-tag__ai-icon"
      name="ai"
      v-tooltip="{ content: $t('document-tag.ai-eval-grid-tooltip'), placement: 'top', offset: '8px' }"
    />

    <!-- Phones: its actions in a sheet, as a row's ⋯ (docs/page-layout.md). -->
    <BaseMoreActions
      v-if="isPhone && actionable"
      :title="document.name"
      :actions="documentActions"
      @select="onDocumentAction"
    />
    <v-menu v-else v-model="actionMenu" absolute>
      <template v-slot:activator="{ on, attrs }">
        <div v-if="actionable" class="document-tag__action" @click.stop v-on="on" v-bind="attrs">
          <BaseIcon class="document-tag__action-icon" name="more-horizontal" />
        </div>
      </template>
      <div class="document-tag__actions-menu base-actions-menu">
        <ul class="base-actions-menu__ul">
          <li class="base-actions-menu__li" @click="downloadDocument">
            <BaseIcon :size="44" name="download" />
            {{ $t('actions.download') }}
          </li>
          <li v-if="showDelete" class="base-actions-menu__li delete" @click="deleteDocument">
            <BaseIcon :size="44" name="trash-2" />
            {{ $t('actions.delete') }}
          </li>
        </ul>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import { viewport } from '@/util/viewport';

import { getDocumentIcon } from '@/util/document-file-util';

export default {
  name: 'BaseDocumentTag',
  components: {
    BaseIcon,
    BaseMoreActions,
  },
  props: {
    icon: {
      type: String,
      required: false,
    },
    document: {
      type: [Object, File, Number],
      required: false,
    },
    actionable: {
      type: Boolean,
      default: false,
    },
    isShort: {
      type: Boolean,
      default: false,
    },
    documentsLength: {
      type: Number,
      required: false,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      actionMenu: false,
      actions: [{ name: 'Rename' }, { name: 'Delete' }],
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    documentActions() {
      return [
        { key: 'download', label: this.$t('actions.download'), icon: 'download' },
        ...(this.showDelete
          ? [{ key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true }]
          : []),
      ];
    },
    ...mapGetters('fieldsExtraction', ['documentsUsed']),
    isUsedByAi() {
      const doc = this.document;
      if (!doc || typeof doc !== 'object' || !doc.id) return false;
      return new Set(this.documentsUsed.map((d) => d.id)).has(doc.id);
    },
  },
  methods: {
    onDocumentAction(key) {
      if (key === 'download') this.downloadDocument();
      else this.deleteDocument();
    },
    getDocumentIcon,
    downloadDocument() {
      this.$emit('download-document', this.document);
    },
    deleteDocument() {
      this.$emit('delete-document', this.document);
    },
    onClick() {
      this.$emit('document-selected', this.document, true);
    },
  },
  watch: {
    actionMenu() {
      // if the action menu got opened then emit a menu open event
      if (this.actionMenu) {
        this.$emit('menu-open', this.document);
      }
    },
  },
};
</script>

<style lang="scss">
.document-tag {
  border-radius: $border-radius-medium;
  color: $color-normal;
  border: 1px $color-neutral-pale solid;
  background-color: $color-neutral-pale;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  transition: all 200ms ease;
  cursor: pointer;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  &.overview {
    min-width: auto;
  }

  &:hover {
    transition: 200ms ease;
    background-color: $color-border;
  }

  &__icon {
    flex: 0 0 auto;
    height: 18px;
    width: 18px;
    color: $color-normal-pale;
  }

  &__text {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  &__check-icon {
    height: 16px;
    width: 16px;
    color: $color-accent;
  }

  &__ai-icon {
    flex: 0 0 auto;
    height: 14px;
    width: 14px;
    color: $color-warm-blue;
  }

  &__action {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    margin-left: 12px;

    &-icon {
      height: 16px;
      width: 16px;
      color: $color-normal-pale;
      transition: color 200ms ease;

      &:hover {
        color: $color-normal;
        transition: color 200ms ease;
      }
    }
  }

  .tooltip {
    max-width: 200px;
  }

  &__actions-menu {
    min-width: 140px;
    cursor: pointer;
  }
}
</style>
